import type {
  LayoutSection,
  SectionDetailResponse,
  SeatCategory,
  ComputedSeatPosition,
  SeatStatus,
  SeatType,
} from '@/types/seating'
import { isUnitSection } from '@/lib/seat-map/seat-pricing'

/**
 * Compute absolute canvas positions for seats in a rectangular section.
 */
function computeRectPositions(
  section: LayoutSection,
  detail: SectionDetailResponse,
  categoryMap: Map<string, SeatCategory>
): ComputedSeatPosition[] {
  const positions: ComputedSeatPosition[] = []

  // Check for precomputed positions from the chart designer layout data
  const posMap = new Map<string, { x: number; y: number; radius?: number }>()
  if ((section as any).rows) {
    for (const row of (section as any).rows) {
      for (const seat of (row.seats || [])) {
        posMap.set(`${row.rowLabel}-${seat.seatNumber}`, { x: seat.x, y: seat.y, radius: seat.radius })
      }
    }
  }

  const seatSize = (section as any).seatSize
  const hidesBg = (section as any).hideBackground === true
  const pad = hidesBg ? 0 : 6
  const innerX = section.x + pad
  const innerY = section.y + pad
  const innerW = section.width - pad * 2
  const innerH = section.height - pad * 2

  const rowCount = detail.rows.length
  if (rowCount === 0) return positions
  const maxSeatsInRow = Math.max(...detail.rows.map(r => r.seats.length), 1)

  const maxRadiusW = (innerW / maxSeatsInRow) / 2.5
  const maxRadiusH = (innerH / rowCount) / 2.5
  const fallbackRadius = Math.min(maxRadiusW, maxRadiusH, 12)
  const radius = seatSize || fallbackRadius

  const rowSpacing = innerH / rowCount
  const seatSpacing = innerW / maxSeatsInRow

  for (let ri = 0; ri < detail.rows.length; ri++) {
    const row = detail.rows[ri]
    const fallbackY = innerY + ri * rowSpacing + rowSpacing / 2

    for (let si = 0; si < row.seats.length; si++) {
      const seat = row.seats[si]
      const key = `${row.rowLabel}-${seat.seatNumber}`
      const pos = posMap.get(key)

      // Use precomputed position if available, otherwise fall back to grid
      const absX = pos ? pos.x : (innerX + si * seatSpacing + seatSpacing / 2)
      const absY = pos ? pos.y : fallbackY
      const seatRadius = pos?.radius || radius

      const cat = seat.categoryId ? categoryMap.get(seat.categoryId) ?? null : null

      positions.push({
        seatId: seat.seatId,
        seatNumber: seat.seatNumber,
        seatIndex: seat.seatIndex,
        rowLabel: seat.rowLabel,
        rowNumber: seat.rowNumber,
        seatType: seat.seatType as SeatType,
        status: seat.status as SeatStatus,
        holdSessionId: (seat as any).holdSessionId ?? null,
        holdType: (seat as any).holdType ?? null,
        channelRestrict: (seat as any).channelRestrict ?? null,
        categoryId: seat.categoryId,
        category: cat ?? seat.category,
        price: seat.price,
        allInPrice: seat.allInPrice ?? null,
        absX,
        absY,
        radius: seatRadius,
        sectionKey: detail.sectionKey,
        sectionName: detail.name,
      })
    }
  }
  return positions
}

/**
 * Compute absolute canvas positions for seats in an arc-shaped section.
 */
function computeArcPositions(
  section: LayoutSection,
  detail: SectionDetailResponse,
  categoryMap: Map<string, SeatCategory>
): ComputedSeatPosition[] {
  const arc = section.arcConfig!
  const positions: ComputedSeatPosition[] = []
  const rowCount = detail.rows.length
  if (rowCount === 0) return positions

  // Prefer the designer's saved positions (rows[].seats[].x/y) — they carry
  // per-row offsets, per-row seat sizes, and skip-number gaps. Recomputing
  // evenly from the DB index (the fallback below) discards all of that and
  // renders arcs differently than the designer preview.
  const posMap = new Map<string, { x: number; y: number; radius?: number }>()
  if ((section as any).rows) {
    for (const row of (section as any).rows) {
      for (const seat of (row.seats || [])) {
        posMap.set(`${row.rowLabel}-${seat.seatNumber}`, { x: seat.x, y: seat.y, radius: seat.radius })
      }
    }
  }

  const radiusStep = (arc.outerRadius - arc.innerRadius) / (rowCount + 1)
  const maxSeatsInRow = Math.max(...detail.rows.map(r => r.seats.length), 1)

  // Radial constraint: seats must fit between rows
  const maxRadiusRadial = radiusStep / 2.5
  // Angular constraint: seats must fit along the innermost (tightest) row
  const innerRowRadius = arc.innerRadius + radiusStep
  const angleRangeRad = ((arc.endAngle - arc.startAngle) * Math.PI) / 180
  const angleStep = angleRangeRad / (maxSeatsInRow + 1)
  const arcDist = innerRowRadius * angleStep
  const maxRadiusAngular = arcDist / 2.5

  const radius = Math.min(maxRadiusRadial, maxRadiusAngular, 12)

  for (let ri = 0; ri < detail.rows.length; ri++) {
    const row = detail.rows[ri]
    const rowRadius = arc.innerRadius + (ri + 1) * radiusStep
    const angleRange = arc.endAngle - arc.startAngle
    const angleStep = angleRange / (row.seats.length + 1)

    for (let si = 0; si < row.seats.length; si++) {
      const seat = row.seats[si]
      const saved = posMap.get(`${row.rowLabel}-${seat.seatNumber}`)
      const angleDeg = arc.startAngle + (si + 1) * angleStep
      const angleRad = (angleDeg * Math.PI) / 180
      const cat = seat.categoryId ? categoryMap.get(seat.categoryId) ?? null : null

      positions.push({
        seatId: seat.seatId,
        seatNumber: seat.seatNumber,
        seatIndex: seat.seatIndex,
        rowLabel: seat.rowLabel,
        rowNumber: seat.rowNumber,
        seatType: seat.seatType as SeatType,
        status: seat.status as SeatStatus,
        holdSessionId: (seat as any).holdSessionId ?? null,
        holdType: (seat as any).holdType ?? null,
        channelRestrict: (seat as any).channelRestrict ?? null,
        categoryId: seat.categoryId,
        category: cat ?? seat.category,
        price: seat.price,
        allInPrice: seat.allInPrice ?? null,
        absX: saved ? saved.x : arc.cx + rowRadius * Math.cos(angleRad),
        absY: saved ? saved.y : arc.cy + rowRadius * Math.sin(angleRad),
        radius: saved?.radius || radius,
        sectionKey: detail.sectionKey,
        sectionName: detail.name,
      })
    }
  }
  return positions
}

/**
 * Compute positions for polygon sections using precomputed positions from layoutData.
 * The chart designer saves exact seat coordinates in rows[].seats[].x/y.
 * Falls back to grid layout for seats missing from layoutData (e.g. rows that
 * failed geometry checks during autofill but were still created in the DB).
 */
function computePolygonPositions(
  section: LayoutSection,
  detail: SectionDetailResponse,
  categoryMap: Map<string, SeatCategory>
): ComputedSeatPosition[] {
  const positions: ComputedSeatPosition[] = []
  // Build a map of precomputed positions from layoutData rows
  const posMap = new Map<string, { x: number; y: number; radius?: number }>()
  if ((section as any).rows) {
    for (const row of (section as any).rows) {
      for (const seat of (row.seats || [])) {
        posMap.set(`${row.rowLabel}-${seat.seatNumber}`, { x: seat.x, y: seat.y, radius: seat.radius })
      }
    }
  }

  // Use the designer's seatSize if set, otherwise derive from spacing
  const seatSpacing = (section as any).seatSpacing || 22
  const radius = (section as any).seatSize || Math.max(1, seatSpacing * 0.4)

  // Track which rows have precomputed positions and which need fallback
  const rowCount = detail.rows.length
  const maxSeatsInRow = Math.max(...detail.rows.map(r => r.seats.length), 1)

  // Fallback grid: evenly distribute rows/seats within the section bounding box
  const hidesBg = (section as any).hideBackground === true
  const pad = hidesBg ? 0 : 6
  const innerX = section.x + pad
  const innerY = section.y + pad
  const innerW = section.width - pad * 2
  const innerH = section.height - pad * 2
  const fallbackRowSpacing = rowCount > 0 ? innerH / rowCount : innerH
  const fallbackSeatSpacing = maxSeatsInRow > 0 ? innerW / maxSeatsInRow : innerW

  for (let ri = 0; ri < detail.rows.length; ri++) {
    const row = detail.rows[ri]
    for (let si = 0; si < row.seats.length; si++) {
      const seat = row.seats[si]
      const key = `${row.rowLabel}-${seat.seatNumber}`
      const pos = posMap.get(key)

      // Use precomputed position if available, otherwise fall back to grid
      const absX = pos ? pos.x : (innerX + si * fallbackSeatSpacing + fallbackSeatSpacing / 2)
      const absY = pos ? pos.y : (innerY + ri * fallbackRowSpacing + fallbackRowSpacing / 2)
      const seatRadius = pos?.radius || radius

      const cat = seat.categoryId ? categoryMap.get(seat.categoryId) ?? null : null
      positions.push({
        seatId: seat.seatId,
        seatNumber: seat.seatNumber,
        seatIndex: si,
        rowLabel: row.rowLabel,
        rowNumber: row.rowNumber,
        seatType: (seat.seatType || 'STANDARD') as SeatType,
        status: (seat.status || 'FREE') as SeatStatus,
        holdSessionId: seat.holdSessionId ?? null,
        categoryId: seat.categoryId ?? null,
        category: cat,
        price: seat.price ?? null,
        allInPrice: seat.allInPrice ?? null,
        absX,
        absY,
        radius: seatRadius,
        sectionKey: section.key,
        sectionName: section.name,
      })
    }
  }
  return positions
}

/**
 * Progressive section seat data cache.
 * Fetches seat data per-section on demand, computes absolute positions,
 * and caches the results for the unified canvas to render.
 */
export class SectionSeatCache {
  private cache = new Map<string, ComputedSeatPosition[]>()
  private loading = new Set<string>()
  private queue: string[] = []
  private accessOrder: string[] = [] // LRU tracking
  private retryMap = new Map<string, number>() // track retries per section
  private pinnedKeys = new Set<string>() // never evict these (focused section)
  private maxConcurrent = 12
  private maxCacheSize = 80 // Max sections in cache
  private eventId: string
  private layoutSections: Map<string, LayoutSection>
  private categoryMap: Map<string, SeatCategory>
  private onLoaded: () => void
  private onError?: (error: string) => void
  private sectionFetchUrlPrefix?: string
  private sectionFetchQuery?: string
  /**
   * Seats the viewer's own session holds. Public seat data carries no session
   * ids (lib/cart/hold-session), so the page tells the cache from its own
   * owner-only answers (hold response, /seats/my-holds) and every seat object
   * loaded now or later is stamped `mine` from this set.
   */
  private ownHolds = new Set<string>()

  constructor(
    eventId: string,
    layoutSections: LayoutSection[],
    categories: SeatCategory[],
    onLoaded: () => void,
    onError?: (error: string) => void,
    sectionFetchUrlPrefix?: string,
    /** Extra query string (no leading '?') appended to every section fetch,
     *  e.g. 'channel=boxoffice' so the seats returned match the availability
     *  the caller's overview counted for that channel. */
    sectionFetchQuery?: string
  ) {
    this.eventId = eventId
    this.layoutSections = new Map(layoutSections.map(s => [s.key, s]))
    this.categoryMap = new Map(categories.map(c => [c.id, c]))
    this.onLoaded = onLoaded
    this.onError = onError
    this.sectionFetchUrlPrefix = sectionFetchUrlPrefix
    this.sectionFetchQuery = sectionFetchQuery
  }

  /** Pre-populate cache from bundled seat data (avoids N+1 API calls) */
  prePopulate(seatSections: Record<string, { sectionKey: string; rows: Array<{ rowLabel: string; rowNumber: number; seats: Array<Record<string, unknown>> }> }>): void {
    for (const [sectionKey, detail] of Object.entries(seatSections)) {
      if (this.cache.has(sectionKey)) continue
      const section = this.layoutSections.get(sectionKey)
      if (!section) continue
      let positions: ComputedSeatPosition[]
      if (section.shape === 'arc' && section.arcConfig) {
        positions = computeArcPositions(section, detail as SectionDetailResponse, this.categoryMap)
      } else if (section.shape === 'polygon') {
        positions = computePolygonPositions(section, detail as SectionDetailResponse, this.categoryMap)
        if (positions.length === 0) positions = computeRectPositions(section, detail as SectionDetailResponse, this.categoryMap)
      } else {
        positions = computeRectPositions(section, detail as SectionDetailResponse, this.categoryMap)
      }
      this.stampOwnHolds(positions)
      this.cache.set(sectionKey, positions)
      this.accessOrder.push(sectionKey)
    }
    this.onLoaded()
  }

  /** Replace the set of seats held by the viewer's own session (see ownHolds). */
  setOwnHolds(seatIds: Iterable<string>): void {
    this.ownHolds = new Set(seatIds)
    for (const [, seats] of this.cache) this.stampOwnHolds(seats, true)
  }

  private stampOwnHolds(seats: ComputedSeatPosition[], clear = false): void {
    if (!clear && this.ownHolds.size === 0) return
    for (const seat of seats) {
      if (this.ownHolds.has(seat.seatId)) seat.mine = true
      else if (clear && seat.mine) seat.mine = false
    }
  }

  has(sectionKey: string): boolean {
    return this.cache.has(sectionKey)
  }

  get(sectionKey: string): ComputedSeatPosition[] | null {
    const result = this.cache.get(sectionKey) ?? null
    if (result) {
      // Update LRU access order
      this.accessOrder = this.accessOrder.filter(k => k !== sectionKey)
      this.accessOrder.push(sectionKey)
    }
    return result
  }

  /** Pin a section key so it's never evicted (call when focusing a section) */
  pin(sectionKey: string): void {
    this.pinnedKeys.clear() // only one pinned at a time
    this.pinnedKeys.add(sectionKey)
  }

  /** Unpin all sections */
  unpin(): void {
    this.pinnedKeys.clear()
  }

  /** Evict least recently used section if cache exceeds max size */
  private evictLRU(): void {
    if (this.cache.size <= this.maxCacheSize) return

    // Find first LRU entry that isn't currently loading or pinned
    const idx = this.accessOrder.findIndex(k => !this.loading.has(k) && !this.pinnedKeys.has(k))
    if (idx >= 0) {
      const lru = this.accessOrder.splice(idx, 1)[0]
      this.cache.delete(lru)
    }
  }

  isLoading(sectionKey: string): boolean {
    return this.loading.has(sectionKey) || this.queue.includes(sectionKey)
  }

  has(sectionKey: string): boolean {
    return this.cache.has(sectionKey)
  }

  /** Refresh LRU position for a section (call during rendering to prevent eviction) */
  touch(sectionKey: string): void {
    if (!this.cache.has(sectionKey)) return
    const idx = this.accessOrder.indexOf(sectionKey)
    if (idx !== -1) this.accessOrder.splice(idx, 1)
    this.accessOrder.push(sectionKey)
  }

  getAllLoaded(): Map<string, ComputedSeatPosition[]> {
    return this.cache
  }

  /**
   * Check if a section should load individual seats (vs. being treated as a unit).
   *
   * This MUST agree with isUnitSection() in seat-pricing and with the click
   * dispatch in UnifiedSeatMap, which both treat a TABLE as whole-table unless
   * it is explicitly 'by-seat'. This method used to enumerate only the
   * 'whole-table' and 'variable' modes, so a TABLE whose bookingMode was never
   * set fell through to `return true`: its seats loaded and drew, the seat
   * hit-test swallowed the click before the section dispatch could fire, and
   * the table sold seat-by-seat on one surface while selling as a unit on the
   * other. Same rule, one place, no drift.
   */
  shouldLoadSeats(sectionKey: string): boolean {
    const section = this.layoutSections.get(sectionKey)
    if (!section) return false
    return !isUnitSection({
      sectionType: section.sectionType,
      tableConfig: section.tableConfig,
    })
  }

  /** Priority request — jumps to front of queue (used when user clicks a section) */
  requestPriority(sectionKey: string): void {
    if (this.cache.has(sectionKey) || this.loading.has(sectionKey)) return
    // Priority requests bypass size threshold — user explicitly clicked this section
    // Remove from queue if already there, then add to front
    this.queue = this.queue.filter(k => k !== sectionKey)
    this.queue.unshift(sectionKey)
    this.processQueue()
  }

  async request(sectionKey: string): Promise<void> {
    if (this.cache.has(sectionKey) || this.loading.has(sectionKey)) return
    if (this.queue.includes(sectionKey)) return

    // Skip loading individual seats for GA, booth, and whole-table sections
    if (!this.shouldLoadSeats(sectionKey)) return

    // Throttle concurrent requests
    if (this.loading.size >= this.maxConcurrent) {
      this.queue.push(sectionKey)
      return
    }

    const section = this.layoutSections.get(sectionKey)
    if (!section) return

    this.loading.add(sectionKey)

    try {
      const path = this.sectionFetchUrlPrefix
        ? `${this.sectionFetchUrlPrefix}/${this.eventId}/seat-map/section/${sectionKey}`
        : `/api/events/${this.eventId}/seat-map/section/${sectionKey}`
      const baseUrl = this.sectionFetchQuery ? `${path}?${this.sectionFetchQuery}` : path
      const res = await fetch(baseUrl)
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: Failed to load section ${sectionKey}`)
      }

      const data = await res.json()

      if (data.success && data.data) {
        const detail: SectionDetailResponse = data.data
        let positions: ComputedSeatPosition[]
        if (section.shape === 'arc' && section.arcConfig) {
          positions = computeArcPositions(section, detail, this.categoryMap)
        } else if (section.shape === 'polygon') {
          positions = computePolygonPositions(section, detail, this.categoryMap)
          // Fallback to rect if no precomputed positions found
          if (positions.length === 0) positions = computeRectPositions(section, detail, this.categoryMap)
        } else {
          positions = computeRectPositions(section, detail, this.categoryMap)
        }

        this.stampOwnHolds(positions)
        this.cache.set(sectionKey, positions)
        this.accessOrder.push(sectionKey)
        this.evictLRU() // Enforce cache size limit
        this.onLoaded()
      } else {
        throw new Error(data.error || `Failed to load section ${sectionKey}`)
      }
    } catch (err) {
      console.warn(`[SeatCache] Failed to load section ${sectionKey}:`, err)
      // Silently retry once after a short delay instead of showing error toast
      const retryCount = this.retryMap.get(sectionKey) || 0
      if (retryCount < 1) {
        this.retryMap.set(sectionKey, retryCount + 1)
        setTimeout(() => this.request(sectionKey), 1000 + Math.random() * 1000)
      }
    } finally {
      this.loading.delete(sectionKey)
      this.processQueue()
    }
  }

  private processQueue(): void {
    while (this.queue.length > 0 && this.loading.size < this.maxConcurrent) {
      const next = this.queue.shift()!
      this.request(next)
    }
  }

  /** Preload sections adjacent to the given one (by index proximity) */
  preloadNeighbors(sectionKey: string): void {
    const keys = Array.from(this.layoutSections.keys())
    const idx = keys.indexOf(sectionKey)
    if (idx === -1) return

    const neighbors = [keys[idx - 1], keys[idx + 1]].filter(Boolean) as string[]
    for (const key of neighbors) {
      this.request(key) // fire and forget
    }
  }

  /** Apply real-time status changes from polling or admin actions */
  applyStatusChanges(changes: Array<{ seatId: string; status: string; holdSessionId?: string | null; holdType?: string | null }>): void {
    const changeMap = new Map(changes.map(c => [c.seatId, c]))

    for (const [, seats] of this.cache) {
      for (const seat of seats) {
        const change = changeMap.get(seat.seatId)
        if (change) {
          seat.status = change.status as SeatStatus
          if (change.holdSessionId !== undefined) {
            seat.holdSessionId = change.holdSessionId
          } else if (change.status !== 'HELD') {
            // The public feed carries no session ids (lib/cart/hold-session):
            // a seat that is no longer held is nobody's hold, so a staff
            // surface's earlier session id must not outlive it.
            seat.holdSessionId = null
          }
          if (change.holdType !== undefined) {
            seat.holdType = change.holdType
          }
        }
      }
    }
  }

  /** Preload all seated sections eagerly (called on mount) */
  preloadAll(): void {
    for (const [key] of this.layoutSections) {
      if (this.shouldLoadSeats(key)) {
        this.request(key) // queue handles concurrency limiting
      }
    }
  }

  clear(): void {
    this.cache.clear()
    this.loading.clear()
    this.queue = []
  }

  /**
   * Drop the cached seats for a single section so the next access forces a
   * fresh fetch. Used after admin operations (hold apply/release) to ensure
   * the in-memory state matches the server without clearing every section.
   */
  invalidate(sectionKey: string): void {
    this.cache.delete(sectionKey)
    this.loading.delete(sectionKey)
    this.pinnedKeys.delete(sectionKey)
    const idx = this.queue.indexOf(sectionKey)
    if (idx !== -1) this.queue.splice(idx, 1)
  }
}
